/**
 * Everything about the platform the test aims at. All of it lives in one subnet, so a firewall
 * that blocks the platform normally blocks the whole range rather than a single host.
 *
 * The hostnames here are real endpoints and stay as they are; what the page *shows* the visitor is
 * the derived name in `@/lib/brand`, because the platform's own brand is not public.
 */

import { BRAND } from '@/lib/brand'

export const PLATFORM_SUBNET = '62.192.160.0/24'

/** Hosts probed on TCP 443. Reaching any one of them proves the signalling path is open. */
export const TCP_TARGETS = [
	{ label: 'API', host: 'api.telecomx.dk' },
	{ label: 'Realtime (softphone)', host: 'realtime.telecomx.dk' },
	{ label: 'Lyd / optagelser', host: 'audio.telecomx.dk' },
	{ label: 'Provisionering', host: 'prov.telecomx.dk' }
] as const

/** The platform's own STUN servers — the only ones that prove UDP reaches the subnet itself. */
export const PLATFORM_STUN = [
	'stun:mgw1.telecomx.dk:3478',
	'stun:mgw2.telecomx.dk:3478'
]

/** Asked only when the platform's own do not answer, to separate a blocked subnet from blocked UDP. */
export const PUBLIC_STUN = [
	'stun:stun.l.google.com:19302',
	'stun:stun.cloudflare.com:3478'
]

/** Unauthenticated endpoint that reports the source IP the platform sees. */
export const IP_ENDPOINT = import.meta.env.VITE_IP_ENDPOINT ?? 'https://api.telecomx.dk/tools/myip'

/** Where the unauthenticated call-test endpoints live. */
export const CALLTEST_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://api.telecomx.dk'

export const TIMEOUTS = {
	tcpFirstTry: 8000,
	tcpRetry: 10_000,
	stun: 9000,
	ipLookup: 8000,
	callTestApi: 15_000,
	/** SIP registration; a network that blocks wss never gets here at all. */
	sipRegister: 12_000,
	/** From dialling to answer. The platform answers immediately when it is healthy. */
	callAnswer: 20_000,
	/** Hard stop on the call, in case the far end never hangs up. The message runs about 20 s. */
	callMax: 45_000,
	/**
	 * How long to keep polling a PENDING result. The server resolves every PENDING within 60 s of
	 * the call ending, so this is that plus room for the round trips.
	 */
	pendingBudget: 70_000
} as const

/** How often the browser reads its own WebRTC counters during the call. */
export const STATS_INTERVAL_MS = 1000

/** Shown to the user as the expected duration of a run: firewall checks plus the recorded call. */
export const ESTIMATED_SECONDS = 45

/** Copied by the "Kopiér reglerne til jeres IT" button. */
export const FIREWALL_RULES = `${BRAND} – firewall-krav til IP-telefoni:
1. UDP connection timeout > 65 sekunder, og SIP ALG / inspection / Helper deaktiveret.
2. Tillad UDP og TCP til/fra ${PLATFORM_SUBNET} på port 80, 443, 3478 og 5000-65535.
3. Tillad UDP port 123 (NTP) til alle IP-adresser (ved brug af bordtelefoner).`
